import React from "react";
import { USER_STATUS_STYLES } from "../statusStyles";
import type { UserDetail } from "../types/users.types";

/** One labelled fact of the row under the name. */
const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline gap-2 min-w-0">
    <span className="text-[14px] text-ink-muted flex-shrink-0">{label}</span>
    <span className="text-[14px] font-medium text-ink truncate">{value}</span>
  </div>
);

interface UserProfileCardProps {
  user: UserDetail;
}

/**
 * Who this account is: the name, what they are on, and the three facts that do
 * not change.
 *
 * The two pills say different kinds of thing and are drawn differently on
 * purpose — the status is a filled tint because it is the one thing on this
 * card somebody might need to act on, and the plan is a plain outline because
 * it is a label, not a warning.
 */
const UserProfileCard: React.FC<UserProfileCardProps> = ({ user }) => {
  return (
    <section className="rounded-md border border-line bg-white">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 p-4 sm:p-6">
        <span className="w-14 h-14 3xl:w-16 3xl:h-16 rounded-full bg-[#DEEBFB] text-[#2F6BAE] flex items-center justify-center flex-shrink-0">
          <span className="text-[20px] 3xl:text-[24px] font-semibold">
            {user.name.charAt(0)}
          </span>
        </span>

        <h2 className="text-[22px] 3xl:text-[26px] font-semibold text-ink min-w-0 truncate">
          {user.name}
        </h2>

        <span
          className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-[12px] font-medium ${
            USER_STATUS_STYLES[user.status]
          }`}
        >
          {user.status}
        </span>

        <span className="inline-flex whitespace-nowrap rounded-full border border-line px-3 py-1 text-[12px] font-medium text-ink">
          {user.plan}
        </span>
      </div>

      {/* The three facts sit on one rule below the name. They wrap into a grid
          rather than a row so the last of them does not fall off a narrow
          window — an admin console is read on laptops as well as monitors.
          There was a DOB here, next to an age worked out from it; the account
          endpoint sends the age and no birthday, so what is left is the age. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 border-t border-line px-4 sm:px-6 py-4">
        <Fact label="Gender" value={user.gender} />
        {/* An account that never gave a birthday has no age, and an em dash is
            the honest answer. */}
        <Fact label="Age" value={user.age ?? "—"} />
        <Fact label="Member since" value={user.joinedOn} />
      </div>
    </section>
  );
};

export default UserProfileCard;
